// VideoFileBlockEditor — uploaded MP4 with controls flags and optional poster.

import { useState } from "react";
import type { VideoFileBlock, Block, MediaWidth } from "../../../lib/blocks";
import { fileUrl } from "../../../lib/pb";
import { MediaPicker } from "../MediaPicker";
import { t } from "../theme";

export interface VideoFileBlockEditorProps {
  block: VideoFileBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
  articleId: string;
  mediaAssets: string[];
  onUploadFile: (file: File) => Promise<string>;
}

const WIDTHS: MediaWidth[] = ["narrow", "regular", "wide", "full"];

export function VideoFileBlockEditor({
  block,
  onChange,
  isSelected,
  articleId,
  mediaAssets,
  onUploadFile,
}: VideoFileBlockEditorProps) {
  const [pickerOpen, setPickerOpen] = useState<"video" | "poster" | null>(null);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleDropFile(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setErr("Only video files");
      return;
    }
    setErr(null);
    try {
      const filename = await onUploadFile(file);
      onChange({ file: filename } as Partial<VideoFileBlock>);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      {isSelected && block.file && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ width: w } as Partial<VideoFileBlock>)}
              style={pillStyle((block.width || "regular") === w)}
            >
              {w}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["autoplay", "loop", "muted"] as const).map((flag) => {
            const value = !!block[flag];
            return (
              <button
                key={flag}
                type="button"
                onClick={() =>
                  onChange({ [flag]: !value } as Partial<VideoFileBlock>)
                }
                style={pillStyle(value)}
              >
                {flag}
              </button>
            );
          })}
        </div>
      )}

      {block.file ? (
        <div>
          <video
            src={fileUrl("articles", articleId, block.file)}
            poster={block.poster ? fileUrl("articles", articleId, block.poster) : undefined}
            controls
            style={{
              width: "100%",
              maxHeight: 480,
              background: "#000",
              borderRadius: 4,
              display: "block",
            }}
          />
          {isSelected && (
            <div style={{ display: "flex", gap: 6, marginTop: 6, fontFamily: t.sans }}>
              <button
                type="button"
                onClick={() => setPickerOpen("video")}
                style={ghostBtnStyle}
              >
                Replace video…
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen("poster")}
                style={ghostBtnStyle}
              >
                {block.poster ? "Replace poster…" : "Set poster…"}
              </button>
              {block.poster && (
                <button
                  type="button"
                  onClick={() => onChange({ poster: undefined } as Partial<VideoFileBlock>)}
                  style={ghostBtnStyle}
                >
                  Clear poster
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            void handleDropFile(f || null);
          }}
          onClick={() => setPickerOpen("video")}
          style={{
            padding: "40px 20px",
            borderRadius: 8,
            border: `2px dashed ${drag ? t.accent : t.border}`,
            background: drag ? t.accentSoft : t.bg,
            color: t.text3,
            textAlign: "center",
            cursor: "pointer",
            fontFamily: t.sans,
            fontSize: 13,
            transition: t.transition,
          }}
        >
          <div style={{ fontSize: 20, color: t.text4, marginBottom: 6 }}>◉</div>
          Drop a video here or click to choose
        </div>
      )}

      {err && (
        <div style={{ marginTop: 6, fontSize: 11, color: t.danger, fontFamily: t.sans }}>
          {err}
        </div>
      )}

      {isSelected && (
        <input
          type="text"
          value={block.caption || ""}
          onChange={(e) => onChange({ caption: e.target.value } as Partial<VideoFileBlock>)}
          placeholder="Caption (optional)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 8,
            padding: "6px 8px",
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 4,
            fontSize: 12,
            color: t.text,
            outline: "none",
            fontFamily: t.sans,
          }}
        />
      )}

      {pickerOpen && (
        <MediaPicker
          articleId={articleId}
          mediaAssets={mediaAssets}
          accept={pickerOpen === "poster" ? "image" : "video"}
          onPick={(filename) => {
            if (pickerOpen === "poster") {
              onChange({ poster: filename } as Partial<VideoFileBlock>);
            } else {
              onChange({ file: filename } as Partial<VideoFileBlock>);
            }
            setPickerOpen(null);
          }}
          onClose={() => setPickerOpen(null)}
          onUploadFile={onUploadFile}
        />
      )}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
  };
}

const ghostBtnStyle: React.CSSProperties = {
  padding: "5px 10px",
  background: "transparent",
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 11,
  color: t.text2,
  cursor: "pointer",
};
